import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import { useCombinedContext } from "../../context/CombinedContext";
import exportToExcel from "../Excel/ExportToExcel";

import StockForm from "./StockForm";
import StockView from "./StockView";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const StockList = () => {
  const [stocks, setStocks] = useState([]);
  const [vessel, setVessel] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [fromDate, setFromDate] = useState(""); // Start date for filtering
  const [toDate, setToDate] = useState(""); // End date for filtering
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(""); // For year-wise filtering
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("stockNo");
  const [sortOrder, setSortOrder] = useState("asc");
  const { selectesVessel, selectedCompany } = useCombinedContext();
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    partNo: "",
    name: "",
  });
  const [searchText, setSearchText] = useState("");

  const companyId = selectedCompany?._id;
  const companyCode = selectedCompany?.company_code;
  const getUniqueValues = (key) => {
    return [...new Set(stocks.map((item) => item[key]).filter(Boolean))];
  };

  // Updated search: filtering based on partNo, name, description, category, unit, or companyCode.
  const handleSearchChange = (e) => {
    setSearchText(e.target.value.toLowerCase());
    setCurrentPage(0);
  };

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/logistics/stock/${companyId}`);
      const stocksArray = response.data.stocks; // Extract the stocks array
      setStocks(stocksArray || []);
      setFilterData(stocksArray || []);
      // console.log("Stock list", stocksArray);
    } catch (error) {
      toast.error("Error fetching stocks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchStocks();
    }
  }, [companyId, fromDate, toDate]);

  const deleteCompany = async (_id) => {
    try {
      await axiosInstance.delete(`/logistics/stocks/${_id}`);
      fetchStocks();
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(`/logistics/stock/${deleteTarget}`);
      setStocks((prev) => prev.filter((loc) => loc._id !== deleteTarget));
      setFilterData((prev) => prev.filter((loc) => loc._id !== deleteTarget));
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      toast.success("Location deleted successfully");
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  // Toggle sorting order for quantity
  const handleSortQuantity = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  // Apply sorting on filterData based on quantity field
  /*   const sortedData = [...filterData].sort((a, b) => {
    if (sortOrder === "asc") return a.quantity - b.quantity;
    return b.quantity - a.quantity;
  }); */

  const filteredAndSortedData = [...stocks]
    .filter((stock) => {
      return (
        stock.stockNo?.toString().toLowerCase().includes(searchText) ||
        stock.name?.toString().toLowerCase().includes(searchText) ||
        stock.description?.toString().toLowerCase().includes(searchText) ||
        stock.category?.toString().toLowerCase().includes(searchText) ||
        stock.unit?.toString().toLowerCase().includes(searchText) ||
        stock.company_code?.toString().toLowerCase().includes(searchText)
      );
    })
    .filter((item) => {
      return (
        (!filters.partNo || String(item.stockNo) === String(filters.partNo)) &&
        (!filters.name || item.name === filters.name)
      );
    })
    .sort((a, b) => {
      const aVal = a?.[sortField];
      const bVal = b?.[sortField];

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      const bothNumeric =
        aVal !== "" &&
        bVal !== "" &&
        !Number.isNaN(aNum) &&
        !Number.isNaN(bNum);

      let cmp = 0;

      if (bothNumeric) {
        cmp = aNum - bNum;
      } else {
        cmp = String(aVal ?? "").localeCompare(String(bVal ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      return sortOrder === "asc" ? cmp : -cmp;
    });

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // const itemsPerPage = 30;
  /*   const currentStocksItems = sortedData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  ); */
  const currentStocksItems = filteredAndSortedData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );
  const pageCount = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  // Check Role
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData.role;

  function formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return "N/A";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const isEditDisabled =
    userRole === "user" && stocks.userEditCount > 0 && !stocks.editApproved;

  const handleExport = () => {
    const sheetData = [];
    // Create header row for the export file
    sheetData.push([
      "Sl No",
      "Part No",
      "Name",
      "Description",
      "Category",
      "Unit",
      "Gross Weight",
      "Qty (Total)",
      "Qty (Godown A)",
      "Qty (Godown B)",
      "Remarks",
    ]);
    // Add a row for each stock item
    filterData.forEach((item, rowIndex) => {
      const qtyA = Number(item.stockAQuantity || 0);
      const qtyB = Number(item.stockBQuantity || 0);
      sheetData.push([
        rowIndex + 1,
        item.stockNo || "N/A",
        item.name || "N/A",
        item.description || "N/A",
        item.category || "N/A",
        item.unit || "N/A",
        item.grossWeight || "N/A",
        qtyA + qtyB,
        qtyA,
        qtyB,
        item.remark || "N/A",
      ]);
    });
    // Convert data to worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    // Optional: Apply styling (borders and bold headers)
    Object.keys(worksheet).forEach((key) => {
      if (key[0] !== "!") {
        worksheet[key].s = {
          font: { bold: sheetData[0].includes(worksheet[key].v) },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
      }
    });
    // Create workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stocks");
    // Write workbook to binary array and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(dataBlob, "stocks_export.xlsx");
  };

  const requestApproval = async (id) => {
    try {
      await axiosInstance.post(`/logistics/stock/request-approval/${id}`);
      toast.success("Approval requested");
      fetchStocks();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };
  const handleApprove = async (id) => {
    try {
      await axiosInstance.patch(`/logistics/stock/approve/${id}`);
      toast.success("Edit approved successfully");
      fetchStocks(); // refresh data
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to approve edit request",
      );
    }
  };
  return (
    <div>
      <ToastContainer />

      <div className="mb-4 headStyle d-flex  justify-content-between align-items-center">
        <h4 className="mb-0">Stocks</h4>
        <button
          className="button-2"
          onClick={() => {
            if (selectedCompany?.status === false) {
              toast.error(
                "The selected company is inactive. Cannot add a stocks.",
              );
            } else {
              setSelectedData(null);
              setShowForm(true);
            }
          }}
          style={{
            borderRadius: "8px",
            whiteSpace: "nowrap",
            padding: "8px 16px",
          }}
        >
          Add Product
        </button>
      </div>
      {/* <div className="d-flex gap-4">
        <h4>Stocks</h4>
        <div style={{ flex: "0 1 auto" }}>
          <Link to="/logistics/purchaselist">
            <button className="button-2">Purchase List</button>
          </Link>
          <Link to="/stock/report">
            <button className="button-2">Report</button>
          </Link>
        </div>
        <div style={{ flex: "0 1 auto" }}>
          <Link to="/logistics/stockoutlist">
            <button className="button-2">Stock Out List</button>
          </Link>
        </div>
      </div> */}
      <div className="headStyle mt-4 ">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                className="form-control"
                type="search"
                placeholder="Search..."
                style={{
                  maxWidth: "250px",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                value={searchText}
                onChange={handleSearchChange}
              />
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
          </div>

          <div style={{ flex: "0 1 auto" }}>
            <button className="button-2 exxportBtn" onClick={handleExport}>
              Export to Excel
            </button>
          </div>
        </div>

        <div
          className="my-3 rounded table-responsive-sm"
          style={{ overflowX: "auto" }}
        >
          <table id="my-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th className="align-middle">Sl No</th>
                <th
                  className="align-middle"
                  onClick={() => handleSort("stockNo")}
                  style={{ cursor: "pointer" }}
                >
                  Part No {sortOrder === "asc" ? "↑" : "↓"}
                  <select
                    className="form-select form-select-sm mt-1"
                    value={filters.partNo}
                    onChange={(e) =>
                      setFilters({ ...filters, partNo: e.target.value })
                    }
                  >
                    <option value="">All</option>
                    {getUniqueValues("stockNo").map((val, i) => (
                      <option key={i} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </th>
                <th className="align-middle">
                  Name
                  <select
                    className="form-select form-select-sm mt-1"
                    value={filters.name}
                    onChange={(e) =>
                      setFilters({ ...filters, name: e.target.value })
                    }
                  >
                    <option value="">All</option>
                    {getUniqueValues("name").map((val, i) => (
                      <option key={i} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </th>
                <th className="align-middle">Description</th>
                <th className="align-middle">Category</th>
                <th className="align-middle">Unit</th>
                <th className="align-middle">Specification</th>
                <th
                  className="align-middle"
                  onClick={() => handleSort("stockAQuantity")}
                  style={{ cursor: "pointer" }}
                >
                  Qty (Total)
                  {sortOrder === "asc" ? "↑" : "↓"}
                </th>
                <th className="align-middle">Qty (Godown BB)</th>
                <th className="align-middle">Qty (Godown TY)</th>
                <th className="align-middle">Remarks</th>
                <th>Status</th>
                <>
                  <th className="text-center align-middle">Actions</th>
                </>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : currentStocksItems.length === 0 ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-1">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentStocksItems.map((stock, index) => (
                  <tr key={stock._id || index}>
                    <td style={{ textAlign: "center" }}>
                      {currentPage * itemsPerPage + index + 1}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {stock.stockNo || "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {stock.name || "N/A"}
                    </td>
                    <td>{stock.description || "N/A"}</td>
                    <td style={{ textAlign: "center" }}>
                      {stock.category || "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {stock.unit || "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {stock.grossWeight || "N/A"}
                    </td>
                    <td style={{ textAlign: "center", fontWeight: 600 }}>
                      {(Number(stock.stockAQuantity || 0) + Number(stock.stockBQuantity || 0)).toLocaleString()}
                    </td>
                    <td style={{ textAlign: "center", color: "#1565c0" }}>
                      {Number(stock.stockAQuantity || 0).toLocaleString()}
                    </td>
                    <td style={{ textAlign: "center", color: "#2e7d32" }}>
                      {Number(stock.stockBQuantity || 0).toLocaleString()}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {stock.remark || "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {stock.editRequestPending
                        ? "🔴 Pending"
                        : stock.editApproved
                          ? "🟢 Approved"
                          : "—"}
                    </td>
                    <>
                      <td>
                        <div className="d-flex justify-content-center align-items-center gap-2">
                          {!(
                            userRole === "user" &&
                            stock.userEditCount > 0 &&
                            !stock.editApproved
                          ) && (
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={() => {
                                  setSelectedData(stock);
                                  setShowForm(true);
                                }}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </button>
                            )}

                          {userRole === "user" &&
                            stock.userEditCount > 0 &&
                            !stock.editApproved && (
                              <>
                                {stock.editRequestPending ? (
                                  <span
                                    style={{
                                      background: "red",
                                      borderRadius: 5,
                                      color: "white",
                                      padding: "5px 10px",
                                    }}
                                  >
                                    Pending Approval
                                  </span>
                                ) : (
                                  <button
                                    className="approvalBtn"
                                    onClick={() => requestApproval(stock._id)}
                                  >
                                    Request Approval
                                  </button>
                                )}
                              </>
                            )}
                          {["admin", "superadmin"].includes(userRole) &&
                            stock.editRequestPending && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleApprove(stock._id)}
                                title="Approve Edit Request"
                              >
                                <i className="bi bi-check2-circle"></i> Approve
                              </button>
                            )}
                          {["admin", "superadmin"].includes(userRole) && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(stock._id)}
                            >
                              <i className="bi bi-trash3"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
        {isViewMode && (
          <StockView
            stocks={selectedData}
            isViewMode={isViewMode}
            userRole={userRole}
            setIsViewMode={setIsViewMode}
          />
        )}
        {showForm && (
          <StockForm
            stock={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            vessel={vessel}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowForm(false);
              fetchStocks();
            }}
          />
        )}
        {isDeleteModalOpen && (
          <div
            className={`modal fade ${isDeleteModalOpen ? "show d-block" : ""}`}
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="deleteModalLabel"
            aria-hidden={!isDeleteModalOpen}
            onClick={(e) => {
              if (e.target.classList.contains("modal"))
                setIsDeleteModalOpen(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="deleteModalLabel">
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setIsDeleteModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body modalWhite">
                  Are you sure you want to delete this location? This action
                  cannot be undone.
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsDeleteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={confirmDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
    </div>
  );
};

export default StockList;
